import { useState } from "react";
import { toast } from "sonner";
import { PauseCircle, PlayCircle, ChevronRight, Stethoscope, Building2 } from "lucide-react";
import { formatBRL, PLAN_META, STATUS_META } from "@/data/backofficeData";
import { CustomerDetail } from "./CustomerDetail";

const Badge = ({ meta, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.color}`}>
    {meta.dot && <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />}
    {meta.label}
  </span>
);

export const MasterAdmin = ({ customers, setCustomers }) => {
  const [selectedId, setSelectedId] = useState(null);
  const selected = customers.find((c) => c.id === selectedId);

  const togglePause = (e, c) => {
    e.stopPropagation();
    const next = c.status === "pausado" ? "ativo" : "pausado";
    setCustomers((list) => list.map((x) => (x.id === c.id ? { ...x, status: next } : x)));
    toast.success(next === "pausado" ? "Assinatura pausada" : "Assinatura reativada", { description: c.name });
  };

  if (selected) {
    return <CustomerDetail customer={selected} setCustomers={setCustomers} onBack={() => setSelectedId(null)} onRemoved={() => setSelectedId(null)} />;
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="master-admin">
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
        <div>
          <h2 className="font-heading text-base font-bold text-slate-900">Assinantes VeloClin</h2>
          <p className="text-xs text-slate-500">{customers.length} contas · clique em um cliente para ver os detalhes completos</p>
        </div>
      </div>

      <div className="hidden grid-cols-[1.6fr_0.8fr_1fr_0.9fr_1fr_1fr] gap-4 border-b border-slate-100 bg-slate-50/60 px-6 py-3 lg:grid">
        {["Cliente / Clínica", "Plano", "Status", "Mensalidade", "Consultas/mês", "Ações"].map((h, i) => (
          <span key={h} className={`text-[11px] font-semibold uppercase tracking-wide text-slate-400 ${i === 5 ? "text-right" : ""}`}>{h}</span>
        ))}
      </div>

      <div className="divide-y divide-slate-100">
        {customers.length === 0 && (
          <p className="py-14 text-center text-sm text-slate-400">Nenhum assinante na base.</p>
        )}
        {customers.map((c) => (
          <div
            key={c.id}
            data-testid={`customer-row-${c.id}`}
            role="button"
            onClick={() => setSelectedId(c.id)}
            className="grid cursor-pointer grid-cols-1 gap-3 px-6 py-4 transition-colors hover:bg-slate-50/70 lg:grid-cols-[1.6fr_0.8fr_1fr_0.9fr_1fr_1fr] lg:items-center"
          >
            <div className="flex items-center gap-3">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${c.type === "clinica" ? "bg-blue-50 text-blue-600" : "bg-violet-50 text-violet-600"}`}>
                {c.type === "clinica" ? <Building2 className="h-4 w-4" /> : <Stethoscope className="h-4 w-4" />}
              </div>
              <div>
                <p className="font-semibold text-slate-900">{c.name}</p>
                <p className="text-xs text-slate-400">{c.contact} · {c.doctors} médico(s)</p>
              </div>
            </div>
            <div><Badge meta={PLAN_META[c.plan]} testid={`customer-plan-${c.id}`} /></div>
            <div><Badge meta={STATUS_META[c.status]} testid={`customer-status-${c.id}`} /></div>
            <p className="font-mono-num text-sm font-bold text-slate-900">{formatBRL(c.mrr)}</p>
            <p className="flex items-center gap-1.5 font-mono-num text-sm text-slate-600">
              <Stethoscope className="h-3.5 w-3.5 text-slate-300" />{c.consultasMes.toLocaleString("pt-BR")}
            </p>
            <div className="flex gap-2 lg:justify-end">
              <button
                data-testid={`customer-pause-${c.id}`}
                onClick={(e) => togglePause(e, c)}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
              >
                {c.status === "pausado" ? <><PlayCircle className="h-3.5 w-3.5 text-emerald-600" />Reativar</> : <><PauseCircle className="h-3.5 w-3.5 text-amber-600" />Pausar</>}
              </button>
              <button
                data-testid={`customer-view-${c.id}`}
                onClick={(e) => { e.stopPropagation(); setSelectedId(c.id); }}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
              >
                Detalhes <ChevronRight className="h-3.5 w-3.5 text-blue-600" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
